import { ActivePromotion } from '../../graphQL/commons'
import { ICartProduct, IConditionAction, IConfig } from '../../interfaces'
import { IProduct } from '../../interfaces/catalog'
import { setProductsQuantity } from '../utilsForCatalogReducer'
import { getMultiPromotionUiDetails } from './getMultiPromotionUiDetails'
import { getPromotionsShortcuts } from './getPromotionsShortcuts'
import {
  conditionFilterVolumePromotions,
  getVolumePromotionsResume,
} from './getVolumePromotionsResume'

export interface ITransformSelectedProductArgs {
  product: IProduct
  config?: IConfig
  activePromotions: ActivePromotion[]
  flattedCartItems?: ICartProduct[]
}

export const SHORTCUT_VOLUME_PROMOTION = 'SHORTCUT_VOLUME_PROMOTION'

const cartProductTransformers: IConditionAction<
  ITransformSelectedProductArgs,
  IProduct
>[] = [
  {
    condition: ({ config, product: { promotions } }) =>
      !!config?.options?.productDetailView &&
      Array.isArray(promotions) &&
      promotions.length > 0 &&
      promotions.filter((promotion) =>
        conditionFilterVolumePromotions(promotion),
      ).length > 0,
    transformAction: ({
      product,
      activePromotions,
      config,
      flattedCartItems,
    }) => {
      const { promotion, ...restProduct } = product
      // promotions,
      const volumePromotionsResume = getVolumePromotionsResume({
        promotions: product.promotions || [],
        activePromotions,
        config,
        productPrice: product.price || 0,
        sku: product.sku,
        flattedCartItems,
        productQuantity: product.quantity || 0,
      })

      const {
        resume,
        promotionDetails: { title = '', description = '' },
      } = volumePromotionsResume!

      // const promotionsWithoutVolume =
      //   promotions?.filter(
      //     (promotion) => !conditionFilterVolumePromotions(promotion),
      //   ) || []

      return {
        ...restProduct,
        // promotions: promotionsWithoutVolume,
        volumePromotionsResume: resume,
        promotionsShortcuts: [
          {
            id: SHORTCUT_VOLUME_PROMOTION,
            title,
            description,
            data: { promotion: null },
            showButton: false,
          },
        ],
      }
    },
  },
  {
    condition: ({ config, product: { promotions, volumePromotionsResume } }) =>
      !!promotions?.length &&
      !!config?.options?.productDetailView &&
      !!config?.options?.splitPromotionTagCatalog &&
      !volumePromotionsResume,
    transformAction: ({
      product: { promotionsShortcuts: existingShortcuts = [], ...restProduct },
      config,
    }) => {
      const promotionsShortcuts = getPromotionsShortcuts({
        product: restProduct,
        config,
        includeAllPromotions: !!config?.options?.splitPromotionTagCatalog,
      })
      return {
        ...restProduct,
        promotionsShortcuts: [...promotionsShortcuts, ...existingShortcuts],
      }
    },
  },
  {
    condition: ({ config, product: { promotions, volumePromotionsResume } }) =>
      !!promotions?.length &&
      !!config?.options?.productDetailView &&
      !!config?.options?.splitPromotionTagCatalog &&
      !!volumePromotionsResume,
    transformAction: ({
      product: { promotionsShortcuts: existingShortcuts = [], ...restProduct },
      config,
    }) => {
      const promotionsWithoutVolume =
        restProduct.promotions?.filter(
          (promotion) => !conditionFilterVolumePromotions(promotion),
        ) || []
      const promotionsShortcuts = getPromotionsShortcuts({
        product: { ...restProduct, promotions: promotionsWithoutVolume },
        config,
        includeAllPromotions: !!config?.options?.splitPromotionTagCatalog,
      })
      return {
        ...restProduct,
        promotionsShortcuts: [...promotionsShortcuts, ...existingShortcuts],
      }
    },
  },
  {
    condition: ({ config, product: { promotions } }) =>
      !!promotions?.length &&
      !!config?.options?.productDetailView &&
      !config?.options?.splitPromotionTagCatalog,
    transformAction: ({
      product: {
        promotionsShortcuts: existingPromotionsShortcuts = [],
        ...product
      },
      config,
    }) => {
      const promotionsShortcuts = getPromotionsShortcuts({
        product,
        config,
        includeAllPromotions: !!config?.options?.splitPromotionTagCatalog,
      })

      return {
        ...product,
        promotionsShortcuts: [
          ...promotionsShortcuts,
          ...existingPromotionsShortcuts,
        ],
      }
    },
  },
  {
    condition: ({ config, product: { promotions } }) =>
      !config?.options?.productDetailView &&
      Number(promotions?.length) > 1 &&
      !!config,
    transformAction: ({ product, config }) => {
      const promotionsDetails = getMultiPromotionUiDetails({
        config: config!,
        product,
      })
      return {
        ...product,
        promotionsDetails,
      }
    },
  },
  {
    condition: ({ product: { promotions }, config }) =>
      !!config?.options?.productDetailView &&
      !config?.options?.splitPromotionTagCatalog &&
      !!Array.isArray(promotions) &&
      promotions.length > 1 &&
      promotions.filter(
        (promotion) => !conditionFilterVolumePromotions(promotion),
      ).length > 0 &&
      !!config,
    transformAction: ({ product, config }) => {
      const promotionsNonVolume = product!.promotions!.filter(
        (promotion) => !conditionFilterVolumePromotions(promotion),
      )
      const promotionsDetails = getMultiPromotionUiDetails({
        config: config!,
        product: {
          ...product,
          promotions: promotionsNonVolume,
        },
      })
      return {
        ...product,
        promotionsDetails,
      }
    },
  },
  /**
   * Remove 'promotion' property from the product when a promotion exists
   * and the productDetailView option is enabled in config.
   * on promotion detail page, we don't need to show the promotion details
   * cause we show shortcuts instead
   */
  {
    condition: ({ product: { promotion }, config }) => !!promotion && !!config?.options?.productDetailView,
    transformAction: ({ product: { promotion, ...restProduct } }) => {
      return {
        ...restProduct,
      }
    },
  },
]

export function transformSelectedProduct({
  product: { promotionsShortcuts, ...product },
  config,
  flattedCartItems,
  activePromotions,
}: ITransformSelectedProductArgs): IProduct {
  const [productWithQuantity] = setProductsQuantity({
    products: [product],
    config,
    flattedCartItems,
    activePromotions,
  })

  const result = cartProductTransformers.reduce<IProduct>(
    (acc, { condition, transformAction }) => {
      const argsTransformers = {
        product: acc,
        config,
        activePromotions,
        flattedCartItems,
      }
      return condition(argsTransformers)
        ? transformAction(argsTransformers)
        : acc
    },
    productWithQuantity,
  )

  return result
}

export default transformSelectedProduct
